import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Noto Sans';
    font-style: normal;
    font-weight: 400 900;
    font-display: swap;
    src: url('https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNr5TRA.woff2') format('woff2');
  }
  * {
      padding: 0px;
      margin: 0px;
      font-family: 'Inter', 'Noto Sans', 'Segoe UI', Arial, sans-serif;
      outline: none;
      -webkit-font-smoothing: antialiased;
      font-feature-settings: 'cv11', 'ss01';
    }
  /* Tokens CCO — paleta X/Twitter. Reagem ao theme.title (light/dark)
     do ThemeProvider, entao alternar o tema via Switch da sidebar
     re-renderiza o dashboard com paleta apropriada. */
  :root {
    --cco-bg: ${(props) =>
      props.theme.title === 'light' ? '#ffffff' : '#0a0a0b'};
    --cco-surface: ${(props) =>
      props.theme.title === 'light' ? '#f7f9f9' : '#0e0f12'};
    --cco-line: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.10)'
        : 'rgba(255, 255, 255, 0.08)'};
    --cco-line-strong: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.18)'
        : 'rgba(255, 255, 255, 0.14)'};
    --cco-ink: ${(props) =>
      props.theme.title === 'light' ? '#0f1419' : '#e7e9ea'};
    --cco-ink-mute: ${(props) =>
      props.theme.title === 'light' ? '#536471' : '#71767b'};
    --cco-ink-faint: ${(props) =>
      props.theme.title === 'light' ? '#8b98a5' : '#4a4e54'};
    /* Cores semanticas constantes entre light/dark (chips, status). */
    --cco-orange: #f1a25b;
    --cco-orange-soft: rgba(241, 162, 91, 0.14);
    --cco-blue: #4ea1f3;
    --cco-blue-soft: rgba(78, 161, 243, 0.14);
    --cco-cyan: #1cb0d8;
    --cco-cyan-soft: rgba(28, 176, 216, 0.14);
    --cco-green: #2bd47d;
    --cco-red: #f4445c;
    --cco-yellow: #f1c349;
    --cco-gray: #5a6068;
    --cco-pad: 24px;
    --cco-row-h: 56px;

    /* Superficies translucidas tematicas — usadas em inputs, hover de
       botoes, fundos sutis dentro de cards/modais. Branco-alpha sobre
       light vira invisivel; aqui invertemos pra preto-alpha no light. */
    --cco-soft-surface: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.04)'};
    --cco-soft-surface-2: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.025)'
        : 'rgba(255, 255, 255, 0.025)'};
    --cco-soft-surface-hover: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.06)'
        : 'rgba(255, 255, 255, 0.06)'};

    /* Tipografia fluida — escala com a largura da viewport via clamp():
       clamp(min_mobile, preferido_vw, max_desktop). Sem media-query
       manual — o tamanho cresce/encolhe suave entre phone e desktop.
       Fonte unica de verdade da escala. Componentes usam var(--fs-*)
       no lugar de px fixo pra fonte responsiva.
         caption  ~10-12  (legendas, hints, badges pequenos)
         label    ~11-13  (labels de campo, chips, th)
         body     ~13-15  (texto corrido, celulas, inputs)
         subtitle ~14-17  (subtitulos, descricoes)
         title    ~16-22  (titulos de card/section)
         heading  ~20-30  (titulo de pagina)
         display  ~24-44  (hero/numeros grandes) */
    --fs-caption: clamp(10px, 0.55vw + 8.5px, 12px);
    --fs-label: clamp(11px, 0.6vw + 9.3px, 13px);
    --fs-body: clamp(13px, 0.55vw + 11.5px, 15px);
    --fs-subtitle: clamp(14px, 0.7vw + 12px, 17px);
    --fs-title: clamp(16px, 1.1vw + 12.5px, 22px);
    --fs-heading: clamp(20px, 2.2vw + 13px, 30px);
    --fs-display: clamp(24px, 4vw + 14px, 44px);
  }

  /* Responsividade dos tokens — telas menores ganham padding reduzido
     pra nao gastar espaco horizontal limitado. Como sao CSS vars, todo
     componente que usa var(--cco-pad) reage automaticamente.
     - Default (desktop): 24px
     - Tablet (<=1100px): 18px
     - Mobile (<=720px): 14px
     - Mobile pequeno (<=480px): 10px
     - cco-row-h tambem reduz pra deixar mais conteudo na tela. */
  @media (max-width: 1100px) {
    :root {
      --cco-pad: 18px;
    }
  }
  @media (max-width: 720px) {
    :root {
      --cco-pad: 14px;
      --cco-row-h: 52px;
    }
  }
  @media (max-width: 480px) {
    :root {
      --cco-pad: 10px;
      --cco-row-h: 48px;
    }
  }
  html {
    scroll-behavior: smooth;
    margin: 0;
    /* Backdrop do html usa o MESMO token do design CCO (--cco-bg) que
       as telas redesenhadas. Antes apontava pra theme.colors.background
       (palette legada: #181818 dark / #F3F1F1 light), tom diferente do
       --cco-bg (#0a0a0b / #fff) — qualquer gap no 100vh do dashboard
       deixava o html aparecer como faixa de cor errada no rodape. */
    background: var(--cco-bg);
    transition: background-color 0.2s
    --color-bg: ${(props) => props.theme.colors.background};
    --color-card: ${(props) => props.theme.colors.card};
    --color-primary: ${(props) => props.theme.colors.darkblue};
    --color-accent: ${(props) => props.theme.colors.blue};
    --color-text: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.black
        : props.theme.colors.white};
    --color-muted: ${(props) => props.theme.colors.label};
    --color-border: ${(props) => props.theme.colors.lightergrey || '#f2f2f2'};
    --color-surface-strong: ${(props) =>
      props.theme.colors.darker || props.theme.colors.card};
  }
  body {
    overflow-x: hidden;
    /* Baseline de fonte fluida — componentes que nao definem font-size
       proprio herdam um tamanho que ja escala com a viewport. Os que
       definem continuam mandando (especificidade maior). */
    font-size: var(--fs-body);
  }

  /* Dropdowns nativos: alguns browsers renderizam o popup usando o
     color-scheme do controle, ignorando parcialmente background local
     em option. Esta regra cobre filtros, formularios e paginadores. */
  select,
  option,
  optgroup {
    color-scheme: ${(props) => (props.theme.title === 'light' ? 'light' : 'dark')};
  }

  select option,
  select optgroup {
    background-color: ${(props) =>
      props.theme.title === 'light' ? '#ffffff' : '#0a0a0b'};
    color: ${(props) => (props.theme.title === 'light' ? '#0f1419' : '#e7e9ea')};
  }

  select option:checked {
    background-color: ${(props) =>
      props.theme.title === 'light' ? '#1cb0d8' : '#1cb0d8'};
    color: #071014;
  }

  /* Em <=720px o BottomNav fixo (58px + safe-area) cobriria o rodape do
     conteudo. Reserva esse espaco no wrapper de conteudo do App. */
  @media (max-width: 720px) {
    .app-content {
      padding-bottom: calc(58px + env(safe-area-inset-bottom, 0px));
    }
  }

  /* Scrollbar discreta padrao do sistema (estilo X/Twitter) — aplicada
     globalmente em qualquer elemento scrollavel + html/body. Antes
     era opt-in via mixin discreteScrollbar; agora e default. Telas
     que querem scrollbar diferente sobrescrevem localmente. */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.18)'
        : 'rgba(255, 255, 255, 0.18)'}
      transparent;
  }
  *::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  *::-webkit-scrollbar-track {
    background: transparent;
  }
  *::-webkit-scrollbar-thumb {
    background-color: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.18)'
        : 'rgba(255, 255, 255, 0.18)'};
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
    transition: background-color 150ms ease;
  }
  *::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) =>
      props.theme.title === 'light'
        ? 'rgba(0, 0, 0, 0.35)'
        : 'rgba(255, 255, 255, 0.35)'};
  }
  *::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* garante contraste em temas claro/escuro para textos básicos */
  label, p {
    color: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.darkblue || props.theme.colors.black
        : props.theme.colors.white};
    transition: color 0.2s ease;
  }

  /* Bordas e linhas usam a cor do tema (light/dark) */
  hr,
  .divider,
  .separator,
  .line {
    border: 0;
    border-top: 1px solid var(--color-border);
    background: var(--color-border);
    color: var(--color-border);
  }

  /* Classe utilitária para títulos/primário (substitui cores fixas como #4d69ae) */
  .title,
  .text-primary {
    color: ${(props) =>
      props.theme.title === 'light'
        ? props.theme.colors.darkblue
        : props.theme.colors.lightblue};
    transition: color 0.2s ease;
  }

  input, button, textarea {
    border: none;
  }
  textarea {
    resize: none;
  }
  ul li { list-style: none; }

.fade-in {
animation: fadeIn ease 1s;
-webkit-animation: fadeIn ease 1s;
-moz-animation: fadeIn ease 1s;
-o-animation: fadeIn ease 1s;
-ms-animation: fadeIn ease 1s;
}
@keyframes fadeIn {
  0% {
    opacity:0;
  }
  100% {
    opacity:1;
  }
}

@-moz-keyframes fadeIn {
  0% {
    opacity:0;
  }
  100% {
    opacity:1;
  }
}

@-webkit-keyframes fadeIn {
  0% {
    opacity:0;
  }
  100% {
    opacity:1;
  }
}

@-o-keyframes fadeIn {
  0% {
    opacity:0;
  }
  100% {
    opacity:1;
  }
}

@-ms-keyframes fadeIn {
  0% {
    opacity:0;
  }
  100% {
    opacity:1;
}
}

.fade-out {
animation: fadeOut ease 1s;
-webkit-animation: fadeOut ease 1s;
-moz-animation: fadeOut ease 1s;
-o-animation: fadeOut ease 1s;
-ms-animation: fadeOut ease 1s;
}
@keyframes fadeOut {
  0% {
    opacity:1;
  }
  100% {
    opacity:0;
  }
}

@-moz-keyframes fadeOut {
  0% {
    opacity:1;
  }
  100% {
    opacity:0;
  }
}

@-webkit-keyframes fadeOut {
  0% {
    opacity:1;
  }
  100% {
    opacity:0;
  }
}

@-o-keyframes fadeOut {
  0% {
    opacity:1;
  }
  100% {
    opacity:0;
  }
}

@-ms-keyframes fadeOut {
  0% {
    opacity:1;
  }
  100% {
    opacity:0;
}
}

button {
  text-transform: uppercase;
}


.recharts-surface {
  overflow: visible;
}

@keyframes expand {
  from {width: 64px;}
  to {width: 260px;}
}

@keyframes expandReverse {
  from {width: 260px;}
  to {width: 64px;}
}

@keyframes spin {
    from {transform: rotate(0deg); }
    to {transform: rotate(360deg); }
}

@keyframes spinReverse {
    from {transform: rotate(360deg); }
    to {transform: rotate(0deg); }
}

@keyframes pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
    transition: 0.75s all;
  }
  100%{
    transform: scale(1);
    transition: 0.75s all;
  }
}

.ReactModal__Overlay.ReactModal__Overlay--after-open{
  background-color: rgba(0, 0, 0, 0.50) !important;
}


@keyframes moveGradient {
  50% {
    background-position: 100% 50%;
  }
}

 .recharts-cartesian-axis-tick-line{
   display: none;
 }
 
.rdt_TableRow {
      font-size: calc(0.4vw + 10px);
  }
  .rdt_TableCell {
      padding: 0;
  }
  .rdt_TableCol {
      border-width: 0px 1px 0px 0px;
      padding: 0;
      font-style: normal;
      font-weight: bold !important;
      font-size: calc(0.2vw + 12px);
      display: flex;
      color: ${(props) => props.theme.colors.darkerblue} !important;
  }
  .rdt_TableHead {
      width: 100%;
  }
  .rdt_TableBody {
    overflow-y: auto !important;
    border-radius: 10px;
    height: 1000px;
  }
  .rdt_Table {
    max-height: 75vh;
    background: ${(props) => props.theme.colors.card};
    border-radius: 10px;
    overflow-x: hidden;
    transition: background-color 0.2s ease;
  }
  .rdt_Pagination{
    display: none !important;
  }
  .rdt_TableCol > div, .rdt_TableCell > label, .rdt_TableCell > div {
       margin: 0 auto;
  }

  .rc-pagination-item-active {
    background: transparent;
    color: black;
    font-weight: bold;
  }
  .rc-pagination-item-active {
    border: none;
  }
  .rc-pagination-item:hover {
    border-color: transparent;
  }
  .rc-pagination-item:hover a {
    color: black;
  }
  .rc-pagination-item {
    border: none;
  }
  .rc-pagination-item-active a{
    color: black;
  }

  .gm-style-iw-d{
    max-height: 170px !important;
    overflow: hidden !important;
  }

  .gm-ui-hover-effect{
    z-index: -1;
  }

  .gm-style-iw.gm-style-iw-c{
    padding-left: 0px !important;
    max-height: 170px !important;
  }

  .gm-style-mtc{
    div {
      background: ${(props) => props.theme.colors.card} !important;
      color: ${(props) => props.theme.colors.label} !important;
    }
  }

  .gm-style{
    button {
      background: ${(props) => props.theme.colors.card} !important;
    }
  }

  .gm-svpc{
    background: ${(props) => props.theme.colors.card} !important;
  }
  
  .gm-style-iw.gm-style-iw-c{
    background: ${(props) => props.theme.colors.card} !important;
  }

  .gm-style .gm-style-iw-t::after{
    background: ${(props) => props.theme.colors.card} !important;
    z-index: -1;
  }


  .pulse {
  animation: pulse 2s infinite;
}
  @-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 #F24D74;
  }
  70% {
      -webkit-box-shadow: none;
  }
  100% {
      -webkit-box-shadow: none;
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 #F24D74;
    box-shadow: 0 0 0 0 #F24D74;
  }
  70% {
      -moz-box-shadow: none;
      box-shadow: none;
  }
  100% {
      -moz-box-shadow: none;
      box-shadow: none;
  }
}

.appear-from-top-right {
  animation: appear-from-top-right linear 270ms ;
}
@-webkit-keyframes appear-from-top-right {
  0%{
      border-radius: 50%;
      left: 100%;
      bottom: 100%;
    };
    50% {
      left: 50%;
      bottom: 50%;
      border-radius: 50%;
    }
    100% {
      border-radius: 0;
      left: 0%;
      bottom: 0%;
    }
}
@keyframes appear-from-top-right {
    0%{
      border-radius: 50%;
      left: 100%;
      bottom: 100%;
    };
    50% {
      left: 50%;
      bottom: 50%;
      border-radius: 50%;
    }
    100% {
      border-radius: 0;
      left: 0%;
      bottom: 0%;
    }
}

.dissapear-from-top-right {
  animation: dissapear-from-top-right linear 270ms ;
}
@-webkit-keyframes dissapear-from-top-right {
  0%{
      border-radius: 50%;
      left: 100%;
      bottom: 100%;
    };
    50% {
      left: 50%;
      bottom: 50%;
      border-radius: 50%;
    }
    100% {
      border-radius: 0;
      left: 0%;
      bottom: 0%;
    }
}
@keyframes dissapear-from-top-right {
    0%{
      border-radius: 0;
      left: 0%;
      bottom: 0%;
    };
    50% {
      left: 50%;
      bottom: 50%;
      border-radius: 0;
    }
    100% {
      border-radius: 50%;
      left: 100%;
      bottom: 100%;
    }
}

  `;

export default GlobalStyle;
